import React, { useState, useEffect } from 'react';
import { 
  UserPlus, Mail, Shield, Trash2, RefreshCw, Users, Phone, Building2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import axios from 'axios';

const UsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('operator');
  const [inviteChannelId, setInviteChannelId] = useState(null);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchChannels();
    fetchLimits();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/channels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannels(response.data.channels || []);
    } catch (error) {
      console.error('Failed to fetch channels:', error);
    }
  };

  const fetchLimits = async () => {
    try {
      const response = await axios.get(`${API}/channels/limits/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch limits:', error);
    }
  };

  // Super admin: Switch to view as another organization
  const handleSwitchOrgContext = async (orgId, orgName) => {
    try {
      const response = await axios.post(`${API}/admin/switch-org-context`, 
        { organization_id: orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        toast.success(`Switched to: ${orgName}`);
        window.location.reload();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to switch organization context';
      toast.error(msg);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    // Validate channel assignment for admin
    if (inviteRole === 'admin' && !inviteChannelId) {
      toast.error('Admin must be assigned to a channel');
      return;
    }
    
    setInviting(true);
    try {
      const payload = { 
        email: inviteEmail, 
        role: inviteRole
      };
      
      // Include channel assignment for admin role
      if (inviteRole === 'admin' && inviteChannelId) {
        payload.assigned_channel_id = inviteChannelId;
      }
      
      const response = await axios.post(`${API}/users/invite`, 
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User invited! Temp password: ${response.data.temp_password}`);
      setIsDialogOpen(false);
      setInviteEmail('');
      setInviteRole('operator');
      setInviteChannelId(null);
      fetchUsers();
      fetchLimits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const getChannelName = (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    return channel?.display_name || 'Unknown';
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, 
        JSON.stringify(newRole),
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      toast.success('Role updated');
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update role';
      toast.error(msg);
    }
  };

  const handleUpdateChannelAssignment = async (userId, channelId) => {
    try {
      await axios.put(`${API}/users/${userId}/channel`, 
        { channel_id: channelId || null },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      toast.success('Channel assignment updated');
      fetchUsers();
      fetchChannels(); // Refresh channels to get updated assigned_admin_id
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update channel assignment';
      toast.error(msg);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User removed');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-600';
      case 'admin': return 'bg-blue-600';
      case 'operator': return 'bg-emerald-600';
      default: return 'bg-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  const canManageUsers = currentUser?.role === 'owner' || currentUser?.role === 'admin' || currentUser?.is_super_admin;
  
  // Owner can edit channel assignment for Admin users
  const canEditChannel = (user) => {
    return (currentUser?.role === 'owner' || currentUser?.is_super_admin) 
           && user.id !== currentUser?.id 
           && user.role === 'admin';
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Team Members</h1>
          <p className="text-muted-foreground">Manage your organization's team members</p>
        </div>
        
        {canManageUsers && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-emerald-700 hover:bg-emerald-800" 
                data-testid="invite-user-btn"
                disabled={limits && !limits.users?.can_add}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  Add a new team member to your organization
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    data-testid="invite-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={inviteRole} onValueChange={(v) => {
                    setInviteRole(v);
                    if (v !== 'admin') setInviteChannelId(null);
                  }}>
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="operator">Operator</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {inviteRole === 'admin' 
                      ? 'Admin can manage AI, workflows, and see conversations for their assigned channel'
                      : 'Operator can handle assigned conversations only'
                    }
                  </p>
                </div>
                
                {/* Channel Assignment for Admin role */}
                {inviteRole === 'admin' && channels.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="channel">Assign to Channel *</Label>
                    <Select 
                      value={inviteChannelId || ''} 
                      onValueChange={setInviteChannelId}
                    >
                      <SelectTrigger data-testid="invite-channel-select">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-green-500" />
                          <SelectValue placeholder="Select a channel" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {channels.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Admin will only see conversations and data from this channel
                    </p>
                  </div>
                )}
                
                {inviteRole === 'admin' && channels.length === 0 && (
                  <Alert>
                    <AlertDescription>
                      Add a WhatsApp channel first before inviting an Admin
                    </AlertDescription>
                  </Alert>
                )}
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-emerald-700 hover:bg-emerald-800" 
                    disabled={inviting || (inviteRole === 'admin' && !inviteChannelId)} 
                    data-testid="send-invite-btn"
                  >
                    {inviting ? 'Sending...' : 'Send Invite'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Plan Limits */}
      {limits && (
        <Alert className={limits.users?.can_add ? 'bg-purple-50 border-purple-200' : 'bg-amber-50 border-amber-200'}>
          <AlertDescription className="flex items-center justify-between">
            <span>
              <strong>{limits.plan_display_name} Plan:</strong> {limits.users?.current} of {limits.users?.max === -1 ? 'Unlimited' : limits.users?.max} team members
            </span>
            {!limits.users?.can_add && (
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/pricing'}>
                Upgrade Plan
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Members ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No team members yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  {/* Super admin sees Organization column */}
                  {currentUser?.is_super_admin && <TableHead>Organization</TableHead>}
                  <TableHead>Role</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Joined</TableHead>
                  {canManageUsers && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                          <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                            {user.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        {user.name}
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                        {user.is_super_admin && (
                          <Badge className="bg-purple-100 text-purple-800 text-xs">Super Admin</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    {/* Super admin sees organization name with Switch button */}
                    {currentUser?.is_super_admin && (
                      <TableCell>
                        {user.organization_id ? (
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              <Building2 className="h-3 w-3 mr-1" />
                              {user.organization_name || 'Unknown'}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={() => handleSwitchOrgContext(user.organization_id, user.organization_name || 'Unknown Org')}
                              data-testid={`switch-org-${user.organization_id}`}
                            >
                              Switch
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">No Org</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {/* Role selector - simplified permissions */}
                      {canManageUsers && user.id !== currentUser?.id ? (
                        <Select 
                          value={user.role} 
                          onValueChange={(value) => handleUpdateRole(user.id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="operator">Operator</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {user.role}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Channel assignment - editable by Owner for Admin users */}
                      {canEditChannel(user) ? (
                        <Select 
                          value={user.assigned_channel_id || 'unassigned'} 
                          onValueChange={(value) => handleUpdateChannelAssignment(user.id, value === 'unassigned' ? null : value)}
                        >
                          <SelectTrigger className="w-40">
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3 text-green-500" />
                              <SelectValue placeholder="Select channel" />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Not assigned</SelectItem>
                            {channels.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : user.assigned_channel_id ? (
                        <Badge variant="outline" className="text-xs">
                          <Phone className="h-3 w-3 mr-1" />
                          {getChannelName(user.assigned_channel_id)}
                        </Badge>
                      ) : user.role === 'admin' ? (
                        <span className="text-xs text-amber-600">Not assigned</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">All</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    {canManageUsers && (
                      <TableCell className="text-right">
                        {/* Users can delete others in their org (except themselves) */}
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Role Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Role Permissions
          </CardTitle>
          <CardDescription>Understanding what each role can do</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-muted rounded-lg">
              <Badge className="bg-purple-600 mb-2">Owner</Badge>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Full access to all features</li>
                <li>• Manage billing & subscription</li>
                <li>• Configure integrations</li>
                <li>• Invite/remove team members</li>
              </ul>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <Badge className="bg-blue-600 mb-2">Admin</Badge>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Manage conversations</li>
                <li>• Create FAQs & workflows</li>
                <li>• View analytics</li>
                <li>• Invite operators</li>
              </ul>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <Badge className="bg-emerald-600 mb-2">Operator</Badge>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• View assigned conversations</li>
                <li>• Reply to customers</li>
                <li>• Use AI suggestions</li>
                <li>• Limited dashboard access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
